import { CardContent, Grid, Fab, Button, Tooltip } from '@mui/material';
import { Alert, AlertColor } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { withTheme, DefaultTheme } from 'styled-components';
import type { ChartData, ChartOptions } from 'chart.js';

import { Typography, Spacer, ChartWrapper } from 'styles';
import { MinimalCard } from 'components/styled';

import TabsList from 'components/TabsList';
import { LinearBuffer } from 'components/Progress';
import useBulkActionsSelector from 'hooks/useBulkActionsSelector';
import { dataTypes } from '@constants';

interface DataHistoryProps {
  theme?: DefaultTheme;
  data: ChartData<'bar'> | { labels: string[]; datasets: any[] };
  chartOptions: ChartOptions<'bar'> | Record<string, any>;
  tabListValue: number;
  handleTabsChange: (event: React.ChangeEvent<unknown>, newValue: number) => void;
  tabsList: Array<{ label: string; value: string }>;
  selectedDataTypes?: any;
  dataTypesMenuOnItemClick: (value: string) => void;
  getDataIntervalBefore: () => void;
  getDataIntervalAfter: () => void;
  canFetchBefore: boolean;
  canFetchAfter: boolean;
  isLoading: boolean;
  error?: { message?: string } | null;
  showAlert: boolean;
  toggleAlert: () => void;
  severity?: AlertColor;
  selectedDataType: { label: string; value: string };
}

const DataHistory = withTheme(
  ({
    theme: _theme,
    data,
    chartOptions,
    tabListValue,
    handleTabsChange,
    tabsList,
    selectedDataTypes: _selectedDataTypes,
    dataTypesMenuOnItemClick,
    getDataIntervalBefore,
    getDataIntervalAfter,
    canFetchBefore,
    canFetchAfter,
    isLoading,
    error,
    showAlert,
    toggleAlert,
    severity,
    selectedDataType,
  }: DataHistoryProps) => {
    const { BulkActionsSelector: DataTypeSelector } = useBulkActionsSelector({
      selectedItemsIds: [],
      menuItems: dataTypes.map((item) => ({
        ...item,
        onClick: () => {
          dataTypesMenuOnItemClick(item.value);
        },
      })),
      anchorElProps: {
        isDisabled: isLoading,
        renderCustomAnchorEl: () => {
          return <Button>{selectedDataType.label}</Button>;
        },
      },
    });

    return (
      <MinimalCard $hoverEffect={false} sx={{ mb: 6, mt: 6 }}>
        {isLoading ? (
          <LinearBuffer />
        ) : (
          (error?.message && (
            <MinimalCard $hoverEffect={false} sx={{ ml: 4 }}>
              {error.message}
            </MinimalCard>
          )) || (
            <CardContent style={{ height: '37rem', overflow: 'auto' }}>
              <Grid
                container
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Grid size="auto">
                  <Typography variant="h6" gutterBottom>
                    Integration Data History
                  </Typography>
                </Grid>

                <Grid size="auto">{DataTypeSelector}</Grid>
              </Grid>

              <Grid
                style={{
                  visibility: showAlert ? 'visible' : 'hidden',
                  marginTop: '2rem',
                }}
                size="auto"
              >
                <Alert onClose={toggleAlert} severity={severity || 'error'}>
                  You reached the end!
                </Alert>
              </Grid>

              <Grid size={12}>
                <TabsList
                  value={tabListValue}
                  handleChange={handleTabsChange}
                  tabsList={tabsList}
                />
              </Grid>

              <Spacer mb={6} />

              <ChartWrapper>
                <Bar data={data} options={chartOptions} />
                <Grid
                  spacing={5}
                  container
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  <Grid size="auto">
                    <Tooltip title={`Previous ${tabsList[tabListValue].label} `}>
                      <Fab
                        onClick={getDataIntervalBefore}
                        size="small"
                        color="secondary"
                        aria-label="add"
                        disabled={isLoading || !canFetchBefore}
                      >
                        {' '}
                        {'<'}
                      </Fab>
                    </Tooltip>
                  </Grid>
                  <Grid size="auto">
                    <Tooltip title={`Next ${tabsList[tabListValue].label} `}>
                      <Fab
                        onClick={getDataIntervalAfter}
                        size="small"
                        color="secondary"
                        aria-label="add"
                        disabled={isLoading || !canFetchAfter}
                      >
                        {'>'}
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </ChartWrapper>
            </CardContent>
          )
        )}
      </MinimalCard>
    );
  },
);

export default DataHistory;
